/**
 * Flow tag evaluator — applies ranking + metric rules to preview items.
 * Conditions inside a rule are AND-combined, and METRIC conditions also
 * FILTER the ranking pool: `lowest + uniqueClicks >= 50` means "the worst
 * item AMONG those with >= 50 unique clicks" — not "the single worst item,
 * but only if it happens to have >= 50" (which tags nobody the moment a
 * fresh low-traffic element becomes the worst). Rules sharing a groupKey are
 * mutually exclusive (highest orderIndex wins); ungrouped rules all apply.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { TagOverrideContext } from "../tag-rules/override-context";
import { rulesForItem } from "./no-stats";
import { checkRanking, createRankIndex, type FlowRankIndex } from "./ranking";
import { buildFlowTagTotals, type FlowTagTotals } from "./compare-to";
import { buildRuleIndex, metricMatches } from "./rule-index";
import {
  flowTagItemKey,
  isRankingCondition,
  type FlowTagCondition,
  type FlowTagItem,
  type FlowTagResult,
  type FlowTagRule,
} from "./types";

interface EvalCtx {
  totals: FlowTagTotals;
  overrides: TagOverrideContext;
  /** Rank index for a rule — its metric conditions narrow the ranked pool. */
  indexOf(rule: FlowTagRule): FlowRankIndex;
}

function conditionMatches(
  item: FlowTagItem,
  cond: FlowTagCondition,
  rule: FlowTagRule,
  ctx: EvalCtx,
): boolean {
  if (isRankingCondition(cond)) {
    const index = ctx.indexOf(rule);
    const info = index.rankOf(item, cond.scope ?? "path", cond.metric ?? "rawScore");
    return info ? checkRanking(info, cond.op, cond.percent) : false;
  }
  return metricMatches(item, cond, rule, ctx.overrides, ctx.totals);
}

function ruleMatches(item: FlowTagItem, rule: FlowTagRule, ctx: EvalCtx): boolean {
  // A no-stats rule IS its population — reaching it already means the element
  // qualifies (rulesForItem partitioned it here). Conditions on such a rule are
  // rejected at write time and ignored at read time: a legacy row with leftover
  // conditions still tags, rather than silently tagging nobody.
  if (rule.appliesTo === "no-stats") return true;
  if (!rule.conditions?.length) return false;
  return rule.conditions.every((c) => conditionMatches(item, c, rule, ctx));
}

function addTag(out: FlowTagResult, item: FlowTagItem, rule: FlowTagRule): void {
  const key = flowTagItemKey(item.layer, item.itemId, item.pathId);
  const list = out.tagsByKey[key] ?? (out.tagsByKey[key] = []);
  if (!list.includes(rule.name)) list.push(rule.name);
  if (rule.color) out.tagColors[rule.name] = rule.color;
  if (rule.emoji) out.tagEmojis[rule.name] = rule.emoji;
}

const byOrderDesc = (a: FlowTagRule, b: FlowTagRule): number =>
  (b.orderIndex ?? 0) - (a.orderIndex ?? 0);

function applyLayerRules(
  item: FlowTagItem,
  rules: FlowTagRule[],
  ctx: EvalCtx,
  out: FlowTagResult,
): void {
  const groups = new Map<string, FlowTagRule[]>();
  for (const r of rules) {
    if (!r.groupKey) {
      if (ruleMatches(item, r, ctx)) addTag(out, item, r);
      continue;
    }
    const g = groups.get(r.groupKey);
    if (g) g.push(r);
    else groups.set(r.groupKey, [r]);
  }
  for (const group of groups.values()) {
    const winner = [...group]
      .sort(byOrderDesc)
      .find((r) => ruleMatches(item, r, ctx));
    if (winner) addTag(out, item, winner);
  }
}

/**
 * `overrides` accepts a bare country key for the many callers that have only
 * that, or the full context when a campaign is also in scope. Rules already
 * baked by resolveRulesForContext carry no overrides, so passing nothing is
 * correct for them.
 */
export function evaluateFlowTags(
  items: ReadonlyArray<FlowTagItem>,
  rules: ReadonlyArray<FlowTagRule>,
  overrides: TagOverrideContext | string = "",
): FlowTagResult {
  const resolved: TagOverrideContext =
    typeof overrides === "string" ? { countryKey: overrides } : overrides;
  const out: FlowTagResult = { tagsByKey: {}, tagColors: {}, tagEmojis: {} };
  const base = createRankIndex(items);
  const cache = new Map<FlowTagRule, FlowRankIndex>();
  const totals = buildFlowTagTotals(items);
  const ctx: EvalCtx = {
    overrides: resolved,
    totals,
    indexOf(rule) {
      let idx = cache.get(rule);
      if (!idx) {
        idx = buildRuleIndex(items, rule, base, resolved, totals);
        cache.set(rule, idx);
      }
      return idx;
    },
  };
  const active = rules.filter((r) => r.enabled !== false);
  for (const item of items) {
    // A PARTITION by population, not a filter — see no-stats.ts. It replaces the
    // old `isEligible` gate, which dropped every statless element before any
    // rule ran and is why no user-writable rule could reach one.
    const layerRules = rulesForItem(item, active);
    if (layerRules.length) applyLayerRules(item, layerRules, ctx, out);
  }
  return out;
}
